import * as React from "react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  LastUpdated,
  NavRail,
  PageHeader,
  Table,
  type Column,
} from "@ardept/design-system";
import { useEmailDrafts, useMe, type EmailDraftsFilter } from "../api/queries.js";
import type { OperatorEmailDraftDto } from "../api/types.js";
import "./EmailDraftsScreen.css";
import "./screens.css";

const STATUS_OPTIONS = [
  "",
  "pending",
  "guardrail_passed",
  "guardrail_failed",
  "sent",
  "failed",
  "rejected",
] as const;

export function EmailDraftsScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: me } = useMe();

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  // D-URL-01: URL is source of truth for filter state
  const filter: EmailDraftsFilter = useMemo(
    () => ({
      tenant: params.get("tenant") ?? undefined,
      status: params.get("status") ?? undefined,
      after: params.get("after") ?? undefined,
    }),
    [params],
  );

  const { data, isLoading, error, dataUpdatedAt } = useEmailDrafts(filter);

  const drafts = data?.drafts ?? [];

  const columns: Column<OperatorEmailDraftDto>[] = [
    {
      key: "tenant_slug",
      label: "Tenant",
      render: (r) => <span className="mono">{r.tenant_slug}</span>,
    },
    {
      key: "job_id",
      label: "Job",
      render: (r) => (
        <button
          className="link-cell"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/jobs/${r.job_id}`);
          }}
        >
          {r.job_id.slice(0, 8)}
        </button>
      ),
    },
    {
      key: "recipient_email",
      label: "Recipient",
      render: (r) => r.recipient_email,
    },
    {
      key: "subject",
      label: "Subject",
      render: (r) =>
        r.subject.length > 60 ? r.subject.slice(0, 60) + "..." : r.subject,
    },
    {
      key: "message_type",
      label: "Type",
      render: (r) => r.message_type,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <span className={`status-badge status-${r.status}`}>{r.status}</span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (r) => new Date(r.created_at).toLocaleString(),
    },
  ];

  const isEmpty = !isLoading && !error && drafts.length === 0;

  const emptyState = isEmpty ? (
    <EmptyState
      iconName="file-edit"
      title="No email drafts"
      body="Drafts pending operator approval will appear here."
    />
  ) : null;

  const tableFooter = data?.next_cursor ? (
    <div className="tbl-foot">
      <span className="count">
        Showing <b>{drafts.length}</b> drafts
      </span>
      <Button
        variant="ghost"
        onClick={() => {
          const next = new URLSearchParams(params);
          next.set("after", data.next_cursor!);
          setParams(next, { replace: true });
        }}
      >
        Load more
      </Button>
    </div>
  ) : drafts.length > 0 ? (
    <div className="tbl-foot">
      <span className="count">
        Showing <b>{drafts.length}</b> drafts
      </span>
    </div>
  ) : null;

  return (
    <div className="app">
      <NavRail active="drafts" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Email drafts"
          subtitle="Email drafts pending operator approval."
        />
        {dataUpdatedAt && <LastUpdated timestamp={dataUpdatedAt} />}

        <div className="filters">
          <div className="filters__group">
            <label htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              className="filter-select"
              value={filter.status ?? ""}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set("status", e.target.value);
                else next.delete("status");
                next.delete("after");
                setParams(next, { replace: true });
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === "" ? "All statuses" : s}
                </option>
              ))}
            </select>
          </div>
          <div className="filters__spacer" />
          <Button
            variant="ghost"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            Reset filters
          </Button>
        </div>

        {isLoading && !data ? (
          <div className="list-loading">Loading email drafts...</div>
        ) : error ? (
          <div className="list-error">Failed to load email drafts. Try again.</div>
        ) : (
          <Table<OperatorEmailDraftDto>
            columns={columns}
            rows={drafts}
            onRowClick={(row) => navigate(`/email-drafts/${row.id}`)}
            stickyHeader
            emptyState={emptyState}
            footer={tableFooter}
          />
        )}
      </main>
    </div>
  );
}
